import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { UserOrganization } from "@/modules/auth/types";

interface OrganizationState {
	selectedOrgId: string | null;
	organizations: UserOrganization[];
	setOrganizations: (orgs: UserOrganization[]) => void;
	setSelectedOrgId: (id: string) => void;
	getCurrentOrg: () => UserOrganization | null;
	clearOrganizations: () => void;
	isMbos: () => boolean;
	canWriteOrg: (orgId?: string | null) => boolean;
}

// Backend PERMISSION_ADMIN_MAX_LEVEL bilan sinxron (level <= N — ma'muriyat, WRITE)
const ADMIN_WRITE_MAX_LEVEL = 10;

/**
 * Organization Store - Global tashkilot boshqaruvi
 *
 * Features:
 * - Persist to localStorage (selectedOrgId only)
 * - Auto-select first org when organizations loaded
 * - Get current organization helper
 */
export const useOrganizationStore = create<OrganizationState>()(
	persist(
		(set, get) => ({
			// Initial state
			selectedOrgId: null,
			organizations: [],

			/**
			 * Organizations ro'yxatini saqlash
			 * Agar selectedOrgId yo'q bo'lsa - birinchisini avtomatik tanlaydi
			 */
			setOrganizations: (orgs) => {
				set({ organizations: orgs });

				// Auto-select first org if none selected
				if (!get().selectedOrgId && orgs.length > 0) {
					const primaryOrg = orgs.find((o) => o.isPrimary) ?? orgs[0];
					set({ selectedOrgId: primaryOrg.id });
				}
			},

			/**
			 * Tanlangan tashkilotni o'zgartirish
			 */
			setSelectedOrgId: (id) => {
				set({ selectedOrgId: id });
			},

			/**
			 * Joriy tashkilotni olish
			 */
			getCurrentOrg: () => {
				const { organizations, selectedOrgId } = get();
				return organizations.find((o) => o.id === selectedOrgId) ?? null;
			},

			/**
			 * Barcha tashkilotlarni tozalash (logout uchun)
			 */
			clearOrganizations: () => {
				set({
					selectedOrgId: null,
					organizations: [],
				});
			},

			isMbos: () => {
				const { organizations } = get();
				return organizations.some((org) => org.name === "Mbos" && org.parentId === null);
			},

			/**
			 * Foydalanuvchi berilgan tashkilotga yoza oladimi (UX uchun taxmin,
			 * haqiqiy tekshiruv backend'da). A'zo bo'lgan org uchun — o'sha
			 * a'zolik leveli; boshqa (quyi) org uchun — istalgan admin-level
			 * a'zolik yetarli deb qaraladi (daraxt merosi).
			 */
			canWriteOrg: (orgId) => {
				const { organizations, selectedOrgId } = get();
				const targetId = orgId ?? selectedOrgId;
				if (!targetId) {
					return false;
				}
				const membership = organizations.find((o) => o.id === targetId);
				if (membership) {
					return (membership.positionLevel ?? 100) <= ADMIN_WRITE_MAX_LEVEL;
				}
				return organizations.some((o) => (o.positionLevel ?? 100) <= ADMIN_WRITE_MAX_LEVEL);
			},
		}),
		{
			name: "organization-storage",
			partialize: (state) => ({
				// Faqat selectedOrgId ni localStorage da saqlaymiz
				selectedOrgId: state.selectedOrgId,
			}),
		}
	)
);
